import React, { useContext } from 'react';
import { defaultIdAccessor } from '../utils/defaultGetIdentifier';
import { resolveAccessor } from '../utils/resolveAccessor';
import {
  ChartAccessorFunctions,
  ChartAccessors,
  ChartFormatters,
  ChartRootProps,
  WithColorAccessor,
  WithColorAccessorFunctions,
} from './Chart.types';
import { ChartInteractionProvider } from './ChartInteractionProvider';
import { ChartTooltipProvider } from './tooltip/ChartTooltipProvider';

type Props<Datum> = ChartAccessors<Datum> & ChartFormatters<Datum> & WithColorAccessor<Datum>;

type ChartAccessorsContextType<Datum> =
  | (ChartAccessorFunctions<Datum> & ChartFormatters<Datum> & WithColorAccessorFunctions<Datum>)
  | undefined;

const ChartAccessorsContext = React.createContext<ChartAccessorsContextType<any>>(undefined);

export const ChartAccessorsProvider = <Datum,>({
  children,
  valueAccessor,
  labelFormatter,
  idAccessor,
  selectedAccessor,
  valueFormatter,
  colorAccessor,
}: React.PropsWithChildren<Props<Datum>>) => {
  const getValue = React.useMemo(() => resolveAccessor(valueAccessor), [valueAccessor]);
  const getIdentifier = React.useMemo(() => resolveAccessor(idAccessor), [idAccessor]);
  const getSelected = React.useMemo(() => {
    if (!selectedAccessor) return undefined;
    return typeof selectedAccessor === 'function' ? selectedAccessor : (datum: Datum) => Boolean(datum[selectedAccessor]);
  }, [selectedAccessor]);
  const getColor = React.useMemo(() => resolveAccessor(colorAccessor), [colorAccessor]);

  const memoizedAccessors = React.useMemo(
    () => ({
      getValue,
      getIdentifier,
      labelFormatter,
      getSelected,
      valueFormatter,
      getColor,
    }),
    [getValue, getIdentifier, labelFormatter, getSelected, valueFormatter, getColor]
  );

  return <ChartAccessorsContext.Provider value={memoizedAccessors}>{children}</ChartAccessorsContext.Provider>;
};

export const useChartAccessorsContext = <Datum,>() => {
  const ctx = useContext(ChartAccessorsContext) as ChartAccessorsContextType<Datum>;
  if (!ctx) throw new Error('useChartAccessorsContext must be used within a ChartAccessorsProvider');
  return ctx;
};

export const ChartRoot = <Datum,>({
  children,
  onTooltipClose,
  onItemClick,
  highlightedItem,
  onHighlightChange,
  pinOnClick,
  valueAccessor,
  labelFormatter,
  idAccessor = defaultIdAccessor,
  selectedAccessor,
  valueFormatter,
  colorAccessor,
}: React.PropsWithChildren<ChartRootProps<Datum>>) => (
  <ChartAccessorsProvider
    valueAccessor={valueAccessor}
    labelFormatter={labelFormatter}
    idAccessor={idAccessor}
    selectedAccessor={selectedAccessor}
    valueFormatter={valueFormatter}
    colorAccessor={colorAccessor}>
    <ChartTooltipProvider onClose={onTooltipClose}>
      <ChartInteractionProvider
        highlightedItem={highlightedItem}
        onHighlightChange={onHighlightChange}
        onItemClick={onItemClick}
        pinOnClick={pinOnClick}>
        {children}
      </ChartInteractionProvider>
    </ChartTooltipProvider>
  </ChartAccessorsProvider>
);
